import type { Programa } from "@/interfaces/programa.interface";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Calendar as CalendarIcon,
  FileText,
  Gauge,
  Layers,
  Users,
  UserCheck,
} from "lucide-react";

const formatActividadDate = (value?: string | Date | null): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
};

interface ProgramaHeaderProps {
  programa: Programa;
  totalAsignaciones: number;
  onOpenReporte: () => void;
}

export function ProgramaHeader({
  programa,
  totalAsignaciones,
  onOpenReporte,
}: ProgramaHeaderProps) {
  const stats = [
    {
      label: "Cupo máximo",
      value: programa.cupoMaximo ?? 0,
      icon: Users,
    },
    {
      label: "Asientos ocupados",
      value: totalAsignaciones,
      icon: UserCheck,
    },
    {
      label: "Intentos permitidos",
      value: programa.limiteIntentos ?? "—",
      icon: Gauge,
    },
    {
      label: "Actividades",
      value: programa.actividadPrograma?.length ?? 0,
      icon: Layers,
    },
  ];

  const dateRange =
    formatActividadDate(programa.fechaInicio) &&
    formatActividadDate(programa.fechaFin)
      ? `${formatActividadDate(programa.fechaInicio)} – ${formatActividadDate(programa.fechaFin)}`
      : null;

  return (
    <section className="rounded-2xl sm:rounded-3xl border border-black/5 bg-gradient-to-br from-[var(--background)] via-[var(--card-background)] to-[var(--background-muted-secondary)] p-4 sm:p-6 md:p-8 shadow-lg dark:border-white/10">
      <div className="flex flex-col gap-4 sm:gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2 sm:space-y-3">
          <Badge
            variant={programa.isActivo ? "default" : "destructive"}
            className="w-fit text-white"
          >
            {programa.isActivo ? "Programa activo" : "Inactivo"}
          </Badge>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[var(--foreground)]">
            {programa.nombre}
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            {programa.tono || "Tono Neutral / Global"}
          </p>
          {dateRange && (
            <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-3 py-1 text-xs font-medium text-[var(--foreground)] shadow dark:border-white/10 dark:bg-white/5">
              <CalendarIcon className="h-4 w-4" />
              {dateRange}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3 lg:items-end">
          <Button
            variant="outline"
            className="gap-2"
            onClick={onOpenReporte}
          >
            <FileText className="h-4 w-4" />
            Reporte
          </Button>
          <div className="grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-black/5 bg-white/70 p-4 text-center shadow-sm dark:border-white/10 dark:bg-white/5"
              >
                <stat.icon className="mx-auto mb-2 h-5 w-5 text-primary" />
                <p className="text-2xl font-semibold text-[var(--foreground)]">
                  {stat.value}
                </p>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
